import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ClipLoader } from "react-spinners";
import { CreditQuery, CreditStatus, DebtType, PaymentPlans } from "../../Models/Credit";
import { getPaymentPlans } from "../../Services/CreditService";
import { sendEvent } from "../../Utils/firebaseLogger";
import useAuthState from "../../Utils/store";
import Debt from "../Debt/Debt";
import EmptyComponent from "../EmptyComponent/EmptyComponent";
import GridContainer from "../GridContainer";
import Spinner from "../Spinners/Spinner";

const AllDebts = ({
  currentTab,
  onPayClick,
  isHomePage = true,
  gridCount = 3,
  onDataFetched,
  selectable = false,
  selectedIds,
  onToggleSelect,
  onTotalCount,
}: {
  currentTab: number,
  onPayClick?: (creditId: number) => void,
  isHomePage?: boolean,
  gridCount?: number
  onDataFetched?: (payments: PaymentPlans) => boolean;//Returns if handled
  /** CRL-3241 multi-select (home pending tab). */
  selectable?: boolean,
  selectedIds?: number[],
  onToggleSelect?: (debt: DebtType) => void,
  /** Reports the API total item count (meta.total) once known. */
  onTotalCount?: (total: number) => void,
}) => {
  const setPayments = useAuthState((state) => state.setPayments);
  const [paymentPlans, setPaymentPlans] = useState<DebtType[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [size, setSize] = useState<number>(20);
  const [loading, setLoading] = useState(true); // Yüklenme durumu

  useEffect(() => {
    handleAllDebts();
    sendEvent(
      `${currentTab == 1
        ? "view_completed_payments_page"
        : "view_unpaid_payments_page"
      }`
    );
  }, [currentTab]);

  const handleAllDebts = async () => {
    if (totalPages !== null && page > totalPages) {
      setHasMore(false);
      return;
    }
    setLoading(true); // Yüklenme durumunu başlat
    let creditQuery: CreditQuery = {
      status: [CreditStatus.UNPAID, CreditStatus.LATE],
      orderDirection: "asc",
    };
    if (currentTab === 0) {
      creditQuery.status = [CreditStatus.UNPAID, CreditStatus.LATE];
      creditQuery.orderDirection = "asc";
    } else {
      creditQuery.status = [CreditStatus.PAID];
      creditQuery.orderDirection = "desc";
    }

    const res = await getPaymentPlans({ size: size, page: page }, creditQuery);
    if (res?.meta?.total !== undefined) {
      onTotalCount?.(res.meta.total);
    }
    const isHandled = (onDataFetched && res != undefined) ? onDataFetched(res) : false;
    if (!isHandled) {
      setPaymentPlans((prevPosts) => [...prevPosts, ...(res?.data || [])]);
      setPayments(res?.data || []);
      if (totalPages === null) {
        const total = res?.meta?.total ?? 0;
        const totalCalcPage = Math.ceil(total / size);
        setTotalPages(totalCalcPage);
      }
      setTotalPages(totalPages);

      if (res?.meta?.current && res?.meta?.current < size) {
        setHasMore(false);
      }
      setPage((prevPage) => prevPage + 1);
    }
    setLoading(false);
  };

  if (loading && paymentPlans.length === 0) {
    return <Spinner />;
  }

  return paymentPlans.length > 0 ? (
    <InfiniteScroll
      dataLength={paymentPlans.length}
      next={handleAllDebts}
      hasMore={hasMore}
      loader={
        <Stack
          display={"flex"}
          alignItems={"center"}
          className="text-center my-4"
        >
          <ClipLoader
            color="#191B1E"
            size={35}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </Stack>
      }
      style={{ overflow: "visible" }}
    >
      <GridContainer gridCount={gridCount} theme={undefined}>
        {paymentPlans &&
          paymentPlans.length > 0 &&
          paymentPlans.map((debt: DebtType) => (
            <Debt
              paymentPlans={paymentPlans}
              key={debt.id}
              debt={debt}
              onPayClick={
                isHomePage
                  ? undefined
                  : () => onPayClick?.(debt.creditId)
              }
              selectable={selectable}
              selected={selectedIds?.includes(debt.id) ?? false}
              onToggleSelect={() => onToggleSelect?.(debt)}
              detailOnPress={isHomePage}
            />
          ))}
      </GridContainer>
    </InfiniteScroll>
  ) : (
    <EmptyComponent />
  );
};

export default AllDebts;
